// Subscription state for the current tenant. Single source of truth — every
// gate, banner, and billing page should read from here so they stay in sync
// when the user upgrades / cancels in another tab.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useAuth } from '@/lib/auth-context';
import { useCurrentTenant } from './use-tenant';
import {
  computeAccess,
  hasLifetimeProPlan,
  isLifetimeSubscription,
  trialDaysRemaining,
  isExpired,
  type Subscription,
} from '@erp/domain/billing';

const SUBSCRIPTION_KEY = ['current-subscription'] as const;

export function useCurrentSubscription() {
  const ds = useDataService();
  const { user } = useAuth();
  const tenant = useCurrentTenant();
  return useQuery<Subscription | null>({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: () => ds.subscriptions.getCurrent(),
    // Don't query before auth + tenant resolve — would 401 then return null,
    // which the gate would (correctly but uselessly) treat as fail-open.
    enabled: !!user && !!tenant.data,
    staleTime: 60 * 1000,
    retry: 1,
  });
}

export function useCancelSubscription() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ds.subscriptions.cancelCurrent(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SUBSCRIPTION_KEY });
    },
  });
}

/** Convenience: derive the gate decision + trial countdown from the cached
 *  query. Components that just need "should I show the banner?" should use
 *  this instead of plumbing the raw subscription around. */
export function useSubscriptionStatus() {
  const q = useCurrentSubscription();
  const { user } = useAuth();
  const sub = q.data ?? null;
  const isLifetimePro = hasLifetimeProPlan(user?.email) || isLifetimeSubscription(sub);
  const access = isLifetimePro ? 'full' : computeAccess(sub);
  return {
    isLoading: q.isLoading,
    subscription: sub,
    access,
    daysRemaining: isLifetimePro ? null : sub ? trialDaysRemaining(sub) : null,
    expired: isLifetimePro ? false : isExpired(sub),
    isLifetimePro,
  };
}
